// Enhance (D-50): recognising the text of scanned pages, in the browser.
// tesseract.js runs in its own worker, loaded with its engine and the four
// languages (D-3, D-49) from the app's own files, which the service worker
// stores on the first visit: nothing is fetched from elsewhere. The page is
// drawn at its scan's own resolution and read; how each word looks comes
// from the pixels (src/ocr/words.ts). Nothing is written to the PDF here.
import type { Line, PSM, Worker as TesseractWorker } from 'tesseract.js';
import paths from '../generated/vendor-paths.json';
import type { ImageInfo, OcrCutout, OcrWord } from '../engine/worker/protocol.ts';
import { scanImage } from '../ocr/kind.ts';
import { lookOfLine } from '../ocr/words.ts';
import { addSparseLines } from '../ocr/sparse.ts';
import { renderPage, type PdfjsDocument } from './pdfjs.ts';

export { pageKind, type PageKind } from '../ocr/kind.ts';

/** tesseract's page segmentation modes used: automatic layout, and sparse text (text anywhere, in no order). */
const PSM_AUTO = '3' as PSM;
const PSM_SPARSE = '11' as PSM;
/** Words below this confidence are marked as uncertain (tesseract: 0–100). */
export const UNCERTAIN = 70;
/**
 * Below this reading quality the page may be turned: the other three orientations are tried. Quality: the
 * confidence of words of three or more letters, weighted by letters, over all letters read (text read sideways
 * comes out as many confident one- and two-letter "words").
 */
const LOW_QUALITY = 60;
/** The resolution the page is drawn at (pixels per point): the scan's own, within these limits. */
const MIN_SCALE = 150 / 72;
const MAX_SCALE = 600 / 72;
const FALLBACK_SCALE = 300 / 72;
/** At most this many pixels per drawn page (a 600 dpi A4 page is 35 million). */
const MAX_PIXELS = 40e6;

const dist = (a: [number, number], b: [number, number]) => Math.hypot(b[0] - a[0], b[1] - a[1]);

/** Pixels per point to draw the page at: the scan image's own resolution. */
function scaleFor(image: ImageInfo | undefined, pageArea: number): number {
  let s = FALLBACK_SCALE;
  if (image?.pixels) {
    const [w, h] = image.pixels;
    s = Math.max(w / dist(image.quad[0], image.quad[1]), h / dist(image.quad[0], image.quad[3]));
  }
  return Math.min(Math.max(s, MIN_SCALE), MAX_SCALE, Math.sqrt(MAX_PIXELS / pageArea));
}

let worker: Promise<TesseractWorker> | undefined;
/** where tesseract's progress goes (the run in progress) */
let progress: ((share: number) => void) | undefined;
const url = (p: string) => new URL(p, document.baseURI).href;

function tesseract(): Promise<TesseractWorker> {
  worker ??= (async () => {
    const { createWorker, OEM } = (await import('tesseract.js')).default;
    return new Promise<TesseractWorker>((resolve, reject) => {
      const fail = (e: unknown) => reject(new Error(`the text recognition engine could not start (${e instanceof Error ? e.message : String(e)})`));
      createWorker(['eng', 'swe', 'deu', 'ita'], OEM.LSTM_ONLY, {
        workerPath: url(paths.tesseractWorker),
        corePath: url(paths.tesseractCore),
        langPath: url(paths.tessdata),
        gzip: true,
        // Loaded as a file of the app (the CSP allows no other scripts), and its data is not cached a second time.
        workerBlobURL: false,
        cacheMethod: 'none',
        logger: (m) => m.status === 'recognizing text' && progress?.(m.progress),
        // tesseract.js never settles createWorker when a language fails to load, and without a handler it throws the
        // error uncaught: the handler ends the start (later errors also reject their own job, so this does nothing then).
        // shortcut: the half-started tesseract worker is not stopped (no handle to it); only after a broken deploy.
        errorHandler: fail,
      }).then(resolve, fail);
    });
  })();
  worker.catch(() => (worker = undefined));
  return worker;
}

/** Stops tesseract at once (a cancelled run): it is started again for the next one. */
function stop(): void {
  const w = worker;
  worker = undefined;
  void w?.then((x) => x.terminate(), () => undefined);
}

/** `p`, or a rejection as soon as `signal` aborts (tesseract does not answer once stopped). */
function abortable<T>(p: Promise<T>, signal: AbortSignal): Promise<T> {
  if (signal.aborted) return Promise.reject(new DOMException('cancelled', 'AbortError'));
  return new Promise((resolve, reject) => {
    const onAbort = () => reject(new DOMException('cancelled', 'AbortError'));
    signal.addEventListener('abort', onAbort, { once: true });
    p.then(resolve, reject).finally(() => signal.removeEventListener('abort', onAbort));
  });
}

/** The canvas turned `turn` degrees clockwise, and the map from its pixels back to the original's. */
function turned(canvas: HTMLCanvasElement, turn: 0 | 90 | 180 | 270): { canvas: HTMLCanvasElement; back: (x: number, y: number) => [number, number] } {
  const { width: W, height: H } = canvas;
  if (turn === 0) return { canvas, back: (x, y) => [x, y] };
  const out = document.createElement('canvas');
  out.width = turn === 180 ? W : H;
  out.height = turn === 180 ? H : W;
  const ctx = out.getContext('2d')!;
  if (turn === 90) ctx.setTransform(0, 1, -1, 0, H, 0);
  else if (turn === 180) ctx.setTransform(-1, 0, 0, -1, W, H);
  else ctx.setTransform(0, -1, 1, 0, 0, W);
  ctx.drawImage(canvas, 0, 0);
  const back = turn === 90 ? (x: number, y: number): [number, number] => [y, H - x] : turn === 180 ? (x: number, y: number): [number, number] => [W - x, H - y] : (x: number, y: number): [number, number] => [W - y, x];
  return { canvas: out, back };
}

export interface PageResult {
  words: OcrWord[];
  /** the corners of the scan read (user space), so the engine can forget the words when it goes */
  image?: [number, number][];
  /** mean word confidence (0–100) */
  confidence: number;
  /** degrees the page was turned to read it (the four-orientation check) */
  turn: number;
  seconds: number;
}

/**
 * Recognises page `pageIndex` (0-based): draws it at its scan's resolution, reads it (trying the other three
 * orientations when confidence is low), and measures each word's look. `onProgress` gets 0–1.
 */
export async function recognisePage(doc: PdfjsDocument, pageIndex: number, images: ImageInfo[], pageArea: number, signal: AbortSignal, onProgress: (share: number) => void): Promise<PageResult> {
  const t0 = performance.now();
  const image = scanImage(images, pageArea);
  const scale = scaleFor(image, pageArea);
  const canvas = document.createElement('canvas');
  const render = renderPage(doc, pageIndex + 1, canvas, scale, { ratio: 1, annotations: false });
  signal.addEventListener('abort', render.cancel, { once: true });
  try {
    const viewport = await abortable(render.done, signal);
    const w = await abortable(tesseract(), signal);
    // Automatic page layout for the main reading (the worker keeps the last mode set).
    await abortable(w.setParameters({ user_defined_dpi: String(Math.round(scale * 72)), tessedit_pageseg_mode: PSM_AUTO }), signal);
    const read = async (turn: 0 | 90 | 180 | 270, share: [number, number]) => {
      const t = turned(canvas, turn);
      progress = (p) => onProgress(share[0] + p * (share[1] - share[0]));
      const { data } = await abortable(w.recognize(t.canvas, {}, { blocks: true, text: false }), signal);
      const lines: Line[] = (data.blocks ?? []).flatMap((b) => b.paragraphs.flatMap((p) => p.lines));
      const words = lines.flatMap((l) => l.words).filter((x) => x.text.trim());
      const letters = (text: string) => [...text].filter((c) => /[\p{L}\p{N}]/u.test(c)).length;
      const score = words.filter((x) => letters(x.text) >= 3).reduce((a, x) => a + x.confidence * letters(x.text), 0);
      const all = words.reduce((a, x) => a + Math.max(1, letters(x.text)), 0);
      const confidence = words.length ? words.reduce((a, x) => a + x.confidence, 0) / words.length : 0;
      return { turn, t, lines, score, quality: all ? score / all : 0, confidence };
    };
    let best = await read(0, [0, 1]);
    if (best.quality < LOW_QUALITY) {
      // A quarter turn either way first (a page put in sideways); the first that reads well is taken.
      for (const [k, turn] of ([90, 270, 180] as const).entries()) {
        const r = await read(turn, [(k + 1) / 4, (k + 2) / 4]);
        if (r.score > best.score) best = r;
        if (best.quality >= LOW_QUALITY) break;
      }
    }
    // A second reading in sparse-text mode (Bug2 follow-up): headings standing alone that the page layout took for
    // pictures ("Motorcycle" on a cover). Its words are added where the first reading found none.
    progress = undefined;
    await abortable(w.setParameters({ tessedit_pageseg_mode: PSM_SPARSE }), signal);
    const sparse = await abortable(w.recognize(best.t.canvas, {}, { blocks: true, text: false }), signal);
    await abortable(w.setParameters({ tessedit_pageseg_mode: PSM_AUTO }), signal);
    best.lines = addSparseLines(best.lines, (sparse.data.blocks ?? []).flatMap((b) => b.paragraphs.flatMap((p) => p.lines)));
    const ctx = best.t.canvas.getContext('2d', { willReadFrequently: true })!;
    const raster = ctx.getImageData(0, 0, best.t.canvas.width, best.t.canvas.height);
    const toUser = (x: number, y: number): [number, number] => viewport.convertToPdfPoint(...best.t.back(x, y)) as [number, number];
    const words: OcrWord[] = [];
    for (const [n, line] of best.lines.entries()) {
      const looks = lookOfLine(raster, line);
      for (const [k, word] of line.words.entries()) {
        const text = word.text.trim();
        if (!text) continue;
        const { x0, y0, x1, y1 } = word.bbox;
        const look = looks[k];
        words.push({
          text,
          quad: [toUser(x0, y1), toUser(x1, y1), toUser(x1, y0), toUser(x0, y0)],
          baseline: [toUser(...look.baseline[0]), toUser(...look.baseline[1])],
          size: look.size / scale,
          color: look.color.map((c) => c / 255) as [number, number, number],
          serif: look.serif,
          mono: look.mono,
          bold: look.bold,
          confidence: word.confidence,
          line: n,
        });
      }
    }
    return { words, image: image?.quad, confidence: best.confidence, turn: best.turn, seconds: (performance.now() - t0) / 1000 };
  } catch (e) {
    if (signal.aborted) stop();
    throw e;
  } finally {
    progress = undefined;
    canvas.width = 0;
    canvas.height = 0;
  }
}

/**
 * The ink of recognised words, cut out of the page as pdf.js draws it at the scan's resolution, for moving them
 * (M6 step 6): each word's box plus a small margin, as its ink colour with an alpha mask from how dark each pixel is
 * (the paper becomes transparent, so it does not travel along). `words`: each word's box (user space, bottom-left,
 * bottom-right, top-right, top-left) and ink colour (0–1).
 */
export async function cutOut(doc: PdfjsDocument, pageIndex: number, images: ImageInfo[], pageArea: number, words: { quad: [number, number][]; color: [number, number, number] }[]): Promise<OcrCutout[]> {
  const scale = scaleFor(scanImage(images, pageArea), pageArea);
  const canvas = document.createElement('canvas');
  try {
    const viewport = await renderPage(doc, pageIndex + 1, canvas, scale, { ratio: 1, annotations: false }).done;
    const ctx = canvas.getContext('2d', { willReadFrequently: true })!;
    return words.map(({ quad, color }) => {
      const px = quad.map(([x, y]) => viewport.convertToViewportPoint(x, y) as [number, number]);
      const xs = px.map((p) => p[0]);
      const ys = px.map((p) => p[1]);
      const margin = Math.max(2, 0.06 * Math.min(Math.max(...xs) - Math.min(...xs), Math.max(...ys) - Math.min(...ys)));
      const x0 = Math.max(0, Math.floor(Math.min(...xs) - margin));
      const y0 = Math.max(0, Math.floor(Math.min(...ys) - margin));
      const x1 = Math.min(canvas.width, Math.ceil(Math.max(...xs) + margin));
      const y1 = Math.min(canvas.height, Math.ceil(Math.max(...ys) + margin));
      const width = Math.max(1, x1 - x0);
      const height = Math.max(1, y1 - y0);
      const data = ctx.getImageData(x0, y0, width, height).data;
      const lum = new Float32Array(width * height);
      for (let i = 0; i < lum.length; i++) lum[i] = 0.299 * data[i * 4] + 0.587 * data[i * 4 + 1] + 0.114 * data[i * 4 + 2];
      // The paper is the median brightness, the ink the darkest few per cent; in between, partly ink.
      const sorted = Float32Array.from(lum).sort();
      const paper = sorted[Math.floor(sorted.length / 2)];
      const ink = sorted[Math.floor(sorted.length * 0.02)];
      const alpha = new Uint8Array(width * height);
      const span = Math.max(1, paper - ink);
      for (let i = 0; i < lum.length; i++) alpha[i] = Math.round(255 * Math.min(1, Math.max(0, (paper - lum[i]) / span)));
      const rgb = new Uint8Array(width * height * 3);
      const c = color.map((v) => Math.round(v * 255));
      for (let i = 0; i < width * height; i++) rgb.set(c, i * 3);
      const pt = (x: number, y: number) => viewport.convertToPdfPoint(x, y) as [number, number];
      return { quad: [pt(x0, y1), pt(x1, y1), pt(x1, y0), pt(x0, y0)], width, height, rgb: rgb.buffer, alpha: alpha.buffer };
    });
  } finally {
    canvas.width = 0;
    canvas.height = 0;
  }
}
